import { FormEvent, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { PageHeader } from "@/components/PageHeader";
import { TablePagination } from "@/components/TablePagination";
import { useClientPagination } from "@/hooks/useClientPagination";
import {
  APPOINTMENT_DURATION_OPTIONS,
  APPOINTMENT_STATUS_LABELS,
  AppointmentValidationError,
  cancelAppointment,
  createAppointment,
  formatAppointmentTimeRange,
  listAppointmentsForDate,
  listUpcomingAppointments,
  SERVICE_UNITS,
  updateAppointmentStatus,
} from "@/core/appointments/appointment-store";
import type { AppointmentStatus } from "@/core/appointments/types";
import { notifyAppointmentReminder } from "@/core/notifications/appointment-notify";
import {
  loadAppointmentPractitioners,
  listAppointmentPractitionersSync,
  practitionerDisplayName,
} from "@/core/practitioners/practitioner-store";
import type { AppointmentPractitioner } from "@/core/practitioners/types";
import { getPatientById, listPatients, patientDetailPath } from "@/core/patients/patient-store";
import { patientFullName } from "@/core/patients/types";
import { routes } from "@/router/all_routes";

function toDatetimeLocalValue(date: Date): string {
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

export function AppointmentsPage() {
  const [refreshKey, setRefreshKey] = useState(0);
  const [selectedDate, setSelectedDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [patientId, setPatientId] = useState("");
  const [scheduledAt, setScheduledAt] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() + 1);
    d.setHours(9, 0, 0, 0);
    return toDatetimeLocalValue(d);
  });
  const [durationMinutes, setDurationMinutes] = useState<number>(30);
  const [serviceUnit, setServiceUnit] = useState<string>(SERVICE_UNITS[0]);
  const [motif, setMotif] = useState("");
  const [practitionerId, setPractitionerId] = useState("");
  const [notes, setNotes] = useState("");
  const [formError, setFormError] = useState<string | null>(null);
  const [reminderMsg, setReminderMsg] = useState<string | null>(null);
  const [practitioners, setPractitioners] = useState<AppointmentPractitioner[]>(() =>
    listAppointmentPractitionersSync(),
  );

  useEffect(() => {
    let active = true;
    loadAppointmentPractitioners().then((list) => {
      if (active) setPractitioners(list);
    });
    return () => {
      active = false;
    };
  }, []);

  const patients = useMemo(() => {
    void refreshKey;
    return listPatients();
  }, [refreshKey]);

  const dayAppointments = useMemo(() => {
    void refreshKey;
    const date = new Date(`${selectedDate}T12:00:00`);
    return listAppointmentsForDate(date);
  }, [selectedDate, refreshKey]);

  const upcoming = useMemo(() => {
    void refreshKey;
    return listUpcomingAppointments(14);
  }, [refreshKey]);

  const upcomingPagination = useClientPagination(upcoming, [], 15);
  const { pagedItems: pagedUpcoming } = upcomingPagination;

  const dayPagination = useClientPagination(dayAppointments, [selectedDate], 25);
  const { pagedItems: pagedDayAppointments } = dayPagination;

  const timeRangePreview = useMemo(() => {
    if (!scheduledAt) return null;
    return formatAppointmentTimeRange(
      new Date(scheduledAt).toISOString(),
      durationMinutes,
    );
  }, [scheduledAt, durationMinutes]);

  const refresh = () => setRefreshKey((k) => k + 1);

  const handleCreate = (e: FormEvent) => {
    e.preventDefault();
    if (!patientId || !motif.trim()) {
      setFormError("Patient et motif requis.");
      return;
    }
    if (!practitionerId) {
      setFormError("Médecin requis.");
      return;
    }
    try {
      createAppointment({
        patientId,
        scheduledAt: new Date(scheduledAt).toISOString(),
        durationMinutes,
        serviceUnit,
        motif,
        practitionerId,
        notes,
      });
      setMotif("");
      setNotes("");
      setFormError(null);
      refresh();
    } catch (err) {
      if (err instanceof AppointmentValidationError) {
        setFormError(err.message);
      } else {
        setFormError("Impossible de créer le rendez-vous.");
      }
    }
  };

  const handleStatus = (id: string, status: AppointmentStatus) => {
    updateAppointmentStatus(id, status);
    refresh();
  };

  const handleReminder = async (apptId: string) => {
    const appt = dayAppointments.find((a) => a.id === apptId) ?? upcoming.find((a) => a.id === apptId);
    if (!appt) return;
    const patient = getPatientById(appt.patientId);
    if (!patient) {
      setReminderMsg("Patient introuvable.");
      return;
    }
    const doctorName = practitionerDisplayName(appt.practitionerId, appt.practitioner, practitioners);
    const result = await notifyAppointmentReminder({
      appointmentId: appt.id,
      patientId: appt.patientId,
      patientName: patientFullName(patient),
      patientPhone: patient.phone,
      scheduledAt: appt.scheduledAt,
      durationMinutes: appt.durationMinutes,
      serviceUnit: appt.serviceUnit,
      practitioner: doctorName,
      motif: appt.motif,
    });
    setReminderMsg(result.detail);
    refresh();
    window.setTimeout(() => setReminderMsg(null), 4000);
  };

  const handleRemindAllToday = async () => {
    const pending = dayAppointments.filter(
      (a) => a.status === "scheduled" || a.status === "confirmed",
    );
    if (pending.length === 0) {
      setReminderMsg("Aucun rendez-vous à rappeler aujourd'hui.");
      return;
    }
    for (const appt of pending) {
      await handleReminder(appt.id);
    }
    setReminderMsg(`${pending.length} rappel(s) envoyé(s) pour la journée.`);
  };

  return (
    <div>
      <PageHeader
        title="Rendez-vous"
        description="Planification des consultations et suivi des présences — lien dossier patient."
        actions={
          <Link to={routes.patientRecords} className="btn btn-outline-secondary btn-sm">
            Dossiers médicaux
          </Link>
        }
      />

      <div className="row g-3 ph-split-workspace">
        <div className="col-lg-4">
          <div className="ph-invoice-sheet">
            <h3 className="h6 mb-3">Nouveau rendez-vous</h3>
            <form onSubmit={handleCreate} className="d-flex flex-column gap-2">
              <div>
                <label className="form-label small fw-semibold" htmlFor="appt-patient">
                  Patient
                </label>
                <select
                  id="appt-patient"
                  className="form-select form-select-sm"
                  value={patientId}
                  onChange={(e) => setPatientId(e.target.value)}
                  required
                >
                  <option value="">— Choisir —</option>
                  {patients.map((p) => (
                    <option key={p.id} value={p.id}>
                      {patientFullName(p)} ({p.fileNumber})
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="form-label small fw-semibold" htmlFor="appt-doc">
                  Médecin
                </label>
                <select
                  id="appt-doc"
                  className="form-select form-select-sm"
                  value={practitionerId}
                  onChange={(e) => setPractitionerId(e.target.value)}
                  required
                >
                  <option value="">— Choisir un médecin —</option>
                  {practitioners.map((doc) => (
                    <option key={doc.id} value={doc.id}>
                      {doc.displayName} — {doc.department}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="form-label small fw-semibold" htmlFor="appt-datetime">
                  Date & heure
                </label>
                <input
                  id="appt-datetime"
                  type="datetime-local"
                  className="form-control form-control-sm"
                  value={scheduledAt}
                  onChange={(e) => setScheduledAt(e.target.value)}
                  required
                />
                {timeRangePreview && (
                  <div className="form-text">Créneau : {timeRangePreview}</div>
                )}
              </div>
              <div>
                <label className="form-label small fw-semibold" htmlFor="appt-duration">
                  Durée
                </label>
                <select
                  id="appt-duration"
                  className="form-select form-select-sm"
                  value={durationMinutes}
                  onChange={(e) => setDurationMinutes(Number(e.target.value))}
                  required
                >
                  {APPOINTMENT_DURATION_OPTIONS.map((mins) => (
                    <option key={mins} value={mins}>
                      {mins} min
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="form-label small fw-semibold" htmlFor="appt-service">
                  Service
                </label>
                <select
                  id="appt-service"
                  className="form-select form-select-sm"
                  value={serviceUnit}
                  onChange={(e) => setServiceUnit(e.target.value)}
                >
                  {SERVICE_UNITS.map((unit) => (
                    <option key={unit} value={unit}>
                      {unit}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="form-label small fw-semibold" htmlFor="appt-motif">
                  Motif
                </label>
                <input
                  id="appt-motif"
                  className="form-control form-control-sm"
                  value={motif}
                  onChange={(e) => setMotif(e.target.value)}
                  required
                />
              </div>
              <div>
                <label className="form-label small fw-semibold" htmlFor="appt-notes">
                  Notes (optionnel)
                </label>
                <textarea
                  id="appt-notes"
                  className="form-control form-control-sm"
                  rows={2}
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Instructions accueil, préparation patient…"
                />
              </div>
              {formError && <div className="alert alert-warning py-2 mb-0 small">{formError}</div>}
              <button type="submit" className="btn btn-primary btn-sm mt-1">
                Planifier
              </button>
            </form>
          </div>

          <div className="ph-invoice-sheet mt-3">
            <h3 className="h6 mb-2">À venir (14 jours)</h3>
            {upcoming.length === 0 ? (
              <p className="small text-muted mb-0">Aucun rendez-vous planifié.</p>
            ) : (
              <>
              <ul className="list-unstyled small mb-0">
                {pagedUpcoming.map((appt) => {
                  const patient = getPatientById(appt.patientId);
                  const doctorName = practitionerDisplayName(
                    appt.practitionerId,
                    appt.practitioner,
                    practitioners,
                  );
                  return (
                    <li key={appt.id} className="mb-2 pb-2 border-bottom">
                      <div className="fw-medium">
                        {formatAppointmentTimeRange(appt.scheduledAt, appt.durationMinutes)}
                        {" · "}
                        {new Date(appt.scheduledAt).toLocaleDateString("fr-FR", {
                          weekday: "short",
                          day: "numeric",
                          month: "short",
                        })}
                      </div>
                      <div>
                        {patient ? (
                          <Link to={patientDetailPath(patient.id)}>{patientFullName(patient)}</Link>
                        ) : (
                          "—"
                        )}{" "}
                        · {appt.serviceUnit}
                      </div>
                      <div className="text-muted">{doctorName}</div>
                    </li>
                  );
                })}
              </ul>
              <TablePagination
                page={upcomingPagination.page}
                pageSize={upcomingPagination.pageSize}
                totalItems={upcomingPagination.totalItems}
                pageSizeOptions={[15, 25, 50]}
                onPageChange={upcomingPagination.setPage}
                onPageSizeChange={upcomingPagination.setPageSize}
              />
              </>
            )}
          </div>
        </div>

        <div className="col-lg-8">
          <div className="ph-invoice-sheet">
            <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3 ph-appt-day-head">
              <h3 className="h6 mb-0">Agenda du jour</h3>
              <div className="d-flex flex-wrap gap-2 align-items-center w-100 w-lg-auto">
                <button
                  type="button"
                  className="btn btn-outline-primary btn-sm flex-grow-1 flex-lg-grow-0"
                  onClick={() => void handleRemindAllToday()}
                >
                  Rappels SMS du jour
                </button>
                <input
                  type="date"
                  className="form-control form-control-sm flex-grow-1 flex-lg-grow-0"
                  style={{ maxWidth: "11rem" }}
                  value={selectedDate}
                  onChange={(e) => setSelectedDate(e.target.value)}
                />
              </div>
            </div>
            {reminderMsg && <p className="small text-success mb-2">{reminderMsg}</p>}
            {dayAppointments.length === 0 ? (
              <p className="text-muted mb-0">Aucun rendez-vous ce jour.</p>
            ) : (
              <div className="table-responsive ph-diag-table">
                <table className="table table-sm ph-dash-table align-middle mb-0">
                  <thead>
                    <tr>
                      <th>Créneau</th>
                      <th>Patient</th>
                      <th>Médecin</th>
                      <th>Service</th>
                      <th>Motif</th>
                      <th>Statut</th>
                      <th className="text-end">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pagedDayAppointments.map((appt) => {
                      const patient = getPatientById(appt.patientId);
                      const doctorName = practitionerDisplayName(
                        appt.practitionerId,
                        appt.practitioner,
                        practitioners,
                      );
                      return (
                        <tr key={appt.id} className="ph-diag-row">
                          <td data-label="Créneau" className="text-nowrap small">
                            {formatAppointmentTimeRange(appt.scheduledAt, appt.durationMinutes)}
                          </td>
                          <td data-label="Patient">
                            {patient ? (
                              <Link to={patientDetailPath(patient.id)}>{patientFullName(patient)}</Link>
                            ) : (
                              "—"
                            )}
                          </td>
                          <td data-label="Médecin" className="small">
                            {doctorName}
                          </td>
                          <td data-label="Service" className="small">
                            {appt.serviceUnit}
                          </td>
                          <td data-label="Motif" className="small">
                            {appt.motif}
                          </td>
                          <td data-label="Statut">
                            <span className={`hosp-rx-badge hosp-rx-badge--${appt.status === "confirmed" ? "sent_to_pharmacy" : appt.status === "completed" ? "dispensed" : "draft"}`}>
                              {APPOINTMENT_STATUS_LABELS[appt.status]}
                            </span>
                          </td>
                          <td data-label="Actions" className="text-end ph-appt-actions">
                            <div className="btn-group btn-group-sm flex-wrap justify-content-end">
                              {(appt.status === "scheduled" || appt.status === "confirmed") && (
                                <button
                                  type="button"
                                  className="btn btn-outline-secondary"
                                  title={
                                    appt.reminderSentAt
                                      ? `Dernier rappel : ${new Date(appt.reminderSentAt).toLocaleString("fr-FR")}`
                                      : "Envoyer rappel SMS"
                                  }
                                  onClick={() => void handleReminder(appt.id)}
                                >
                                  Rappel SMS
                                </button>
                              )}
                              {appt.status === "scheduled" && (
                                <button
                                  type="button"
                                  className="btn btn-outline-primary"
                                  onClick={() => handleStatus(appt.id, "confirmed")}
                                >
                                  Confirmer
                                </button>
                              )}
                              {(appt.status === "scheduled" || appt.status === "confirmed") && (
                                <>
                                  <button
                                    type="button"
                                    className="btn btn-outline-success"
                                    onClick={() => handleStatus(appt.id, "completed")}
                                  >
                                    Vu
                                  </button>
                                  <button
                                    type="button"
                                    className="btn btn-outline-danger"
                                    onClick={() => cancelAppointment(appt.id)}
                                  >
                                    Annuler
                                  </button>
                                </>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                <TablePagination
                  page={dayPagination.page}
                  pageSize={dayPagination.pageSize}
                  totalItems={dayPagination.totalItems}
                  onPageChange={dayPagination.setPage}
                  onPageSizeChange={dayPagination.setPageSize}
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
